import {
  Backdrop,
  Box,
  CircularProgress,
  Container,
  makeStyles,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Edit as EditIcon,
  Eye as EyeIcon,
  Shield as GatesIcon,
  Trash2 as DeleteIcon,
} from 'react-feather';
import ConfirmationBox from 'src/components/ConfirmationBox';
import ContextMenu, { ContextMenuLinks } from 'src/components/ContextMenu';
import CustomLink from 'src/components/CustomLink';
import Header from 'src/components/Header';
import MUIReactTable from 'src/components/MUIReactTable';
import Page from 'src/components/Page';
import Truncate from 'src/components/Truncate';
import {
  useDeleteStoreMutation,
  useLazyGetStoresQuery,
} from 'src/features/store/api/storeApi';
import { useApiErrorMessage } from 'src/hooks/useApiErrorMessage';
import useAppInsightPageView from 'src/hooks/useAppInsightPageView';
import useCommonStyles from 'src/hooks/useCommonStyles';
import usePolicy from 'src/hooks/usePolicy';
import { Theme } from 'src/theme';
import { DataDTOStoreListing } from 'src/types/api/generated/api';
import { Policies } from 'src/types/policies';
import { Resources } from 'src/types/resources';
import { getSelectedClientId } from 'src/utils/client';
import { truncate } from 'src/utils/common';
import { isExoApiError } from 'src/utils/errorHandlers';
import { buildNestedFilters, buildOrderByString } from 'src/utils/tableQuery';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
}));

const StoreListPage: FC = () => {
  useAppInsightPageView({ name: 'ExoAdmin-Store List' });
  const name = Resources.stores;
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { enqueueSnackbar } = useSnackbar();

  const clientId = getSelectedClientId();

  const canReadStores = usePolicy(Policies.StoreManager);
  const canWriteStores = usePolicy(Policies.Administrator);
  const canUpdateStores = usePolicy(Policies.Administrator);
  const canDeleteStores = usePolicy(Policies.Administrator);

  const [
    getStores,
    {
      data: stores = {
        page: -1,
        totalPages: 1,
        totalCount: 1,
        rows: [],
        pageSize: 1,
        skip: 0,
      },
      isFetching: isFetchingStores,
      error: storesError,
    },
  ] = useLazyGetStoresQuery();
  const [deleteStore] = useDeleteStoreMutation();

  const storesErrorMessage = useApiErrorMessage(storesError);

  useEffect(() => {
    if (storesError && storesErrorMessage) {
      enqueueSnackbar(storesErrorMessage, { variant: 'error', autoHideDuration: 3000 });
    }
  }, [storesError, storesErrorMessage, enqueueSnackbar]);

  const fetchRecords = useCallback(
    ({ pageSize, pageIndex, filters, sortBy }): void => {
      getStores(
        {
          clientId,
          page: pageIndex + 1,
          pageSize: Number(pageSize),
          filters: buildNestedFilters(filters),
          orderBy: buildOrderByString(sortBy),
        },
        true,
      );
    },
    [clientId, getStores],
  );

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [recordSelectedToBeDeleted, setRecordSelectedToBeDeleted] = useState<[number, string]>([
    0,
    '',
  ]);

  const handleDelete = (id: number, storeName: string): void => {
    setRecordSelectedToBeDeleted([id, storeName]);
    setConfirmationDialogOpen(true);
  };

  const handleCloseConfirmDialog = (): void => {
    setConfirmationDialogOpen(false);
  };

  const handleConfirmBoxSubmit = async (): Promise<void> => {
    setConfirmationDialogOpen(false);
    const [id, storeName] = recordSelectedToBeDeleted;
    try {
      await deleteStore({ id }).unwrap();
      enqueueSnackbar(`${truncate(storeName)} has been removed.`, {
        variant: 'success',
        autoHideDuration: 3000,
      });
    } catch (error) {
      enqueueSnackbar(
        isExoApiError(error) ? error.data.detail : `Failed to remove ${truncate(storeName)}.`,
        { variant: 'error', autoHideDuration: 3000 },
      );
    }
  };

  const columns = [
    { Header: 'Name', accessor: 'name', filterable: true, sortable: false },
    { Header: 'Contact Name', accessor: 'contactName', filterable: true, sortable: false },
    { Header: 'Email Address', accessor: 'email', filterable: true, sortable: false },
    { Header: 'Phone Number', accessor: 'phoneNumber', filterable: true, sortable: false },
    { Header: 'Actions', accessor: 'actions' },
  ];

  const makeData = (records: DataDTOStoreListing[]): any[] => {
    return records.map((record) => {
      const contextMenuLinks: ContextMenuLinks[] = [
        {
          to: `/app/management/stores/${record.id}`,
          title: 'View Store',
          icon: () => <EyeIcon size={20} />,
          show: canReadStores,
        },
        {
          to: `/app/management/stores/${record.id}/edit`,
          title: 'Edit Store',
          icon: () => <EditIcon size={20} />,
          show: canUpdateStores,
        },
        {
          to: `/app/management/stores/${record.id}/intelligence-points`,
          title: 'Manage Intelligence Points',
          icon: () => <GatesIcon size={20} />,
          show: canReadStores,
        },
        {
          to: false,
          onClick: () => handleDelete(record.id, record.name),
          title: 'Delete Store',
          icon: () => <DeleteIcon size={20} />,
          show: canDeleteStores,
        },
      ];
      return {
        name: (
          <CustomLink
            link={canReadStores ? `/app/management/stores/${record.id}` : false}
            children={<Truncate value={record.name ?? ''} />}
          />
        ),
        contactName: <Truncate value={record.contactName ?? ''} />,
        email: <Truncate value={record.email ?? ''} />,
        phoneNumber: record.phone ?? '',
        actions: <ContextMenu links={contextMenuLinks} />,
      };
    });
  };

  return (
    <Page className={classes.root} title="Stores List" exoBgLogo={true}>
      <Backdrop className={commonClasses.backdrop} open={stores.page === -1 && isFetchingStores}>
        <CircularProgress color="primary" />
      </Backdrop>
      <Container maxWidth={false}>
        <ConfirmationBox
          title={
            recordSelectedToBeDeleted[1]
              ? `Delete ${truncate(recordSelectedToBeDeleted[1], 75)}?`
              : ''
          }
          open={confirmationDialogOpen}
          handleClose={handleCloseConfirmDialog}
          handleSubmit={handleConfirmBoxSubmit}
        />
        <Header
          enableImport={false}
          breadcrumbs={[]}
          pageTitle={'Manage Stores'}
          pageLink={canWriteStores ? '/management/stores/create' : null}
          addTitle="New Store"
          mainHeader={'Management'}
        />
        <Box mt={3}>
          <MUIReactTable
            name={name}
            loading={false}
            data={makeData(stores.rows)}
            columns={columns}
            requestData={fetchRecords}
            manual={true}
            pageCount={stores.totalPages}
            totalCount={stores.totalCount}
            noRecords={stores.page > 0 && !stores.rows.length}
            refreshing={isFetchingStores}
          />
        </Box>
      </Container>
    </Page>
  );
};

export default StoreListPage;
